import { FC, useCallback, useState } from "react";
import styles from "./TypeComponentCard.module.less";
import { ExpandAltOutlined, ExpandOutlined } from "@ant-design/icons";
import { Button, Modal, Typography, Tooltip } from "antd";
import { useLanguage } from "~/hooks/useLanguage";

interface TypeComponentCardProps {
  title?: React.ReactNode;
  subTitle?: React.ReactNode;
  extraComponent?: React.ReactNode;
  style?: React.CSSProperties;
  children?: React.ReactNode;
  footer?: React.ReactNode;
  fullScreenButtonVisible?: boolean;
  onShowFullScreen?: (fullScreen: boolean) => void;
  isExpandable?: boolean;
  isExpanded?: boolean;
  onToggle?: () => void;
}

const TypeComponentCard: FC<TypeComponentCardProps> = ({
  title = "",
  subTitle,
  extraComponent,
  style,
  children,
  footer,
  fullScreenButtonVisible = true,
  onShowFullScreen,
  isExpandable,
  isExpanded,
  onToggle,
}) => {
  const [fullScreen, setFullScreen] = useState(false);
  const [isModalContentVisible, setIsModalContentVisible] = useState(false);
  const { formatMessage } = useLanguage();

  const onClickFullScreen = useCallback(() => {
    setFullScreen(true);
  }, []);

  const handleAfterOpen = useCallback(() => {
    setIsModalContentVisible(true);
    onShowFullScreen?.(fullScreen);
  }, [fullScreen, onShowFullScreen]);

  const handleCancel = useCallback(() => {
    setFullScreen(false);
    setIsModalContentVisible(false);
    onShowFullScreen?.(false);
  }, [onShowFullScreen]);

  return (
    <div className={styles.TypeComponentCard} style={style}>
      <div className={styles.header}>
        <div className={styles.titleContainer}>
          <div>
            <div className={styles.title}>
              <Typography.Title level={2}>{title}</Typography.Title>
            </div>
            {subTitle && <div className={styles.subTitle}>{subTitle}</div>}
          </div>

          {fullScreenButtonVisible && (
            <div className={styles.fullScreen}>
              <Button
                size="small"
                type={fullScreen ? "dashed" : "text"}
                icon={<ExpandAltOutlined className={styles.fullScreenIcon} />}
                title={formatMessage("TypeComponentCard.Expand")}
                onClick={onClickFullScreen}
              ></Button>
            </div>
          )}
        </div>

        {extraComponent && <div>{extraComponent}</div>}
      </div>
      {!fullScreen && <div className={styles.cardContent}>{children}</div>}
      <div className={styles.footer}>{footer}</div>

      <Modal
        destroyOnClose
        title={
          <div className={styles.headerInModal}>
            <div className={styles.titleContainer}>
              <div className={styles.title}>
                <Typography.Title level={2}>{title}</Typography.Title>
              </div>
              {subTitle && (
                <div className={styles.subTitle}>
                  <Typography.Title level={2}>{subTitle}</Typography.Title>
                </div>
              )}
            </div>
            {extraComponent && (
              <div className={styles.extraComponent}>{extraComponent}</div>
            )}
          </div>
        }
        open={fullScreen}
        width="100vw"
        height="100vh"
        onCancel={handleCancel}
        afterOpenChange={(visible) => {
          if (visible) handleAfterOpen();
          else setIsModalContentVisible(false);
        }}
        styles={{
          content: {
            height: "98vh",
            overflow: "hidden",
            marginTop: "-95px",
            userSelect: "none",
          },
          body: {
            userSelect: "none",
            display: "flex",
            justifyContent: "center",
            alignItems: "center",
            width: "100%",
            height: "95%",
          },
        }}
        footer={null}
      >
        {isModalContentVisible && (
          <div className={styles.cardContent}>{children}</div>
        )}
      </Modal>

      {isExpandable && (
        <Tooltip title={formatMessage("TypeComponentCard.Expand")}>
          <ExpandOutlined
            className={styles.expand}
            onClick={isExpanded ? undefined : onToggle}
          />
        </Tooltip>
      )}
    </div>
  );
};

export default TypeComponentCard;
